/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import { useCallback, useState } from 'react';
import { useSsoErrorReporter } from './useSsoErrorReporter';
import { SCREEN_NAME } from '../config/analytics';
import { useTranslation } from 'react-i18next';
import { CANCEL_DELETION_STATUS } from '../constants/accountRecovery';
import { describeRestoreError } from '../utils/accountRecovery';
import { describeSsoError } from '../utils/ssoErrors';
import {
  EmailLoginMode,
  LoginData,
  LoginError,
  LoginNotification,
  PasswordlessLoginResult,
} from '../widgets/LoginView/types';
import { useLoginByCredentials } from './account';
import { useLoginByCredentials2Fa } from './loginByCredentials2Fa';
import { EmailAuthCredentials } from './useEmailAuthFlow';
import type { KeepAccountRecoveryHandlers } from './useKeepAccountRecovery';
import { useRequestPasswordlessCode } from './useRequestPasswordlessCode';
import { useSignupFeatureFlags } from './useSignupFeatureFlags';

export type TwoFaLoginData = {
  email?: string;
};

export interface UseEmailLoginSubmitOptions {
  /** `PASSWORD` submits credentials; `PASSWORDLESS` requests a one-time code. */
  emailLoginMode: EmailLoginMode;
  /** Awaited so the loader spans the sign-in tail, not just the request. */
  onLoginSuccess?: (data: LoginData) => void | Promise<void>;
  onTwoFactorRequired?: (data: TwoFaLoginData) => void;
  onLoginError?: (errorData: LoginError) => void;
  onPasswordLessEmailLoginSuccess?: (data: PasswordlessLoginResult) => void;
  /** Owned by the view so every login flow shares one notification slot. */
  setNotification: (notification: LoginNotification | null) => void;
  accountRecovery?: KeepAccountRecoveryHandlers;
}

/** The login screen's submit call (email+password & passwordless) . */
export function useEmailLoginSubmit(options: UseEmailLoginSubmitOptions) {
  const {
    emailLoginMode,
    onLoginSuccess,
    onTwoFactorRequired,
    onLoginError,
    onPasswordLessEmailLoginSuccess,
    setNotification,
    accountRecovery,
  } = options;

  const { t } = useTranslation('account');

  // Both created since hooks can't be conditional; the flag picks which runs.
  const { enable_sso_login_2fa_v6 } = useSignupFeatureFlags();
  const legacyLogin = useLoginByCredentials();
  const twoFaLogin = useLoginByCredentials2Fa();
  const isPending = legacyLogin.isPending || twoFaLogin.isPending;
  // Covers the sign-in tail too, so the loader spans past the request.
  const [isFinishing, setIsFinishing] = useState(false);
  const { mutate: passwordlessLoginMutate, isPending: isPasswordlessPending } =
    useRequestPasswordlessCode();

  const reportError = useSsoErrorReporter(SCREEN_NAME.loginV2);

  const showLoginFailed = useCallback(
    (error: Error) => {
      const { message } = describeSsoError(
        error,
        t,
        t('loginPage.loginFailed'),
      );
      reportError(error, message);
      setNotification({ message, type: NotificationViewStyle.ERROR });
    },
    [reportError, setNotification, t],
  );

  const showRestoreFailure = useCallback(
    (error: unknown) => {
      setNotification({
        message: describeRestoreError(error, t),
        type: NotificationViewStyle.ERROR,
      });
    },
    [setNotification, t],
  );

  /** Signs in, or hands off to the OTP step when the account owes 2FA. */
  const runPasswordLogin = useCallback(
    async (credentials: {
      username: string;
      password: string;
      restoreAccount?: boolean;
    }) => {
      // Restore retries go through the same path so 2FA is never skipped.
      if (enable_sso_login_2fa_v6) {
        return twoFaLogin.mutateAsync(credentials);
      }
      await legacyLogin.mutateAsync(credentials);
      return undefined;
    },
    [enable_sso_login_2fa_v6, legacyLogin, twoFaLogin],
  );

  /** Signs in and runs the success tail, or hands off to the 2FA step. */
  const completePasswordLogin = useCallback(
    async (
      email: string | undefined,
      password: string | undefined,
      restoreAccount?: boolean,
    ) => {
      const result = await runPasswordLogin({
        username: email ?? '',
        password: password ?? '',
        restoreAccount,
      });
      setNotification(null);
      if (result?.status === 'twoFactorRequired') {
        onTwoFactorRequired?.({ email });
        setIsFinishing(false);
        return;
      }

      // Stays true: the screen is unmounting, clearing would flash the form.
      await onLoginSuccess?.({ email, password });
    },
    [onLoginSuccess, onTwoFactorRequired, runPasswordLogin, setNotification],
  );

  /** Yes, log in — POST /auth/v2/passwordless { username, status: "cancel_deletion" }. */
  const requestPasswordlessCancelDeletion = useCallback(
    (username: string) => {
      passwordlessLoginMutate(
        {
          username,
          status: CANCEL_DELETION_STATUS,
        },
        {
          onSuccess: (data) => {
            setNotification(null);
            onPasswordLessEmailLoginSuccess?.({
              ...data,
              email: username,
              cancelDeletion: true,
            });
          },
          onError: (retryError: Error) => {
            showRestoreFailure(retryError);
          },
        },
      );
    },
    [
      onPasswordLessEmailLoginSuccess,
      passwordlessLoginMutate,
      setNotification,
      showRestoreFailure,
    ],
  );

  // EMAIL-PASSWORD LOGIN
  const submit = useCallback(
    async ({ email, password }: EmailAuthCredentials) => {
      if (emailLoginMode === EmailLoginMode.PASSWORD) {
        setIsFinishing(true);
        try {
          await completePasswordLogin(email, password);
        } catch (error) {
          onLoginError?.({ method: 'email', error: error as Error });
          setIsFinishing(false);

          if (
            accountRecovery?.tryHandleScheduledDeletion(error, () => {
              void (async () => {
                setIsFinishing(true);
                try {
                  await completePasswordLogin(email, password, true);
                } catch (retryError) {
                  setIsFinishing(false);
                  showRestoreFailure(retryError);
                }
              })();
            })
          ) {
            setNotification(null);
            return;
          }

          showLoginFailed(error as Error);
        }

        return;
      }

      const username = email ?? '';

      // PASSWORDLESS LOGIN
      passwordlessLoginMutate(
        { username },
        {
          onSuccess: (data) => {
            setNotification(null);
            onPasswordLessEmailLoginSuccess?.({ ...data, email });
          },
          onError: (error: Error) => {
            onLoginError?.({ method: 'email', error });

            if (
              accountRecovery?.tryHandleScheduledDeletion(error, () => {
                requestPasswordlessCancelDeletion(username);
              })
            ) {
              setNotification(null);
              return;
            }

            showLoginFailed(error);
          },
        },
      );
    },
    [
      emailLoginMode,
      completePasswordLogin,
      passwordlessLoginMutate,
      onLoginError,
      onPasswordLessEmailLoginSuccess,
      setNotification,
      accountRecovery,
      requestPasswordlessCancelDeletion,
      showLoginFailed,
      showRestoreFailure,
    ],
  );

  return {
    submit,
    /** True for the whole sign-in: request, profile check and navigation. */
    isEmailLoginPending: isPending || isFinishing || isPasswordlessPending,
  };
}
